import { memo, useMemo, useRef, useState } from "react";
import { Panel } from "primereact/panel";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import { SelectButton } from "primereact/selectbutton";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { useReactToPrint } from "react-to-print";
import { useReport } from "../../context/report";

const METRICAS = [
    { label: "Trama", value: "trama" },
    { label: "Urdume", value: "urdume" },
    { label: "Trama + Urdume", value: "total" },
];

const fmt1 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmt0 = new Intl.NumberFormat("pt-BR");

export default memo(function QuebrasOcorrenciasArtigo() {
    const { data } = useReport();

    const [metrica, setMetrica] = useState("trama");
    const [limite, setLimite] = useState(15);
    const [artigos, setArtigos] = useState([]);
    const [ocultarZerados, setOcultarZerados] = useState(false);

    const tabelaRef = useRef(null);
    const [printing, setPrinting] = useState(false);

    const handlePrint = useReactToPrint({
        content: () => tabelaRef.current,
        pageStyle: "@page { size: A4 landscape; margin: 8mm; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }",
        onBeforeGetContent: () =>
            new Promise((resolve) => {
                setPrinting(true);
                setTimeout(resolve, 500);
            }),
        onAfterPrint: () => setPrinting(false),
    });

    // Registros do backend (sem as linhas "Total")
    const registros = useMemo(() => {
        if (!data?.datatable) return [];
        const lista = [];
        data.datatable.forEach((r) => {
            if (r.turma === "Total") return;
            lista.push({
                tear: r.tear,
                artigo: r.artigo === null || r.artigo === undefined || r.artigo === "" ? "-" : String(r.artigo),
                turno: `${String(r.data).trim()}|${r.turma}`,
                trama: Number(r.trama) || 0,
                urdume: Number(r.urdume) || 0,
            });
        });
        return lista;
    }, [data]);

    const artigoOptions = useMemo(
        () =>
            [...new Set(registros.map((r) => r.artigo))]
                .sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }))
                .map((a) => ({ label: a, value: a })),
        [registros]
    );

    const { linhas, totalOcorrencias } = useMemo(() => {
        const corte = limite === null || limite === undefined ? 0 : limite;

        // artigo -> teares e turnos (tear + turno somados)
        const mapa = new Map();
        registros.forEach((r) => {
            if (!mapa.has(r.artigo)) mapa.set(r.artigo, { artigo: r.artigo, teares: new Set(), turnos: new Map() });
            const item = mapa.get(r.artigo);
            item.teares.add(r.tear);
            const chave = `${r.tear}|${r.turno}`;
            if (!item.turnos.has(chave)) item.turnos.set(chave, { tear: r.tear, trama: 0, urdume: 0 });
            const t = item.turnos.get(chave);
            t.trama += r.trama;
            t.urdume += r.urdume;
        });

        let lista = [];
        mapa.forEach((item) => {
            // Trama e urdume são avaliados separadamente contra o limite.
            // Em "Trama + Urdume", soma-se apenas o que atingiu o limite em cada uma.
            const tearesAfetados = new Set();
            let ocorrencias = 0;
            let soma = 0;
            item.turnos.forEach((t) => {
                const tramaOk = t.trama >= corte;
                const urdumeOk = t.urdume >= corte;
                let valor = 0;
                let conta = false;
                if (metrica === "trama") {
                    conta = tramaOk;
                    valor = t.trama;
                } else if (metrica === "urdume") {
                    conta = urdumeOk;
                    valor = t.urdume;
                } else {
                    conta = tramaOk || urdumeOk;
                    valor = (tramaOk ? t.trama : 0) + (urdumeOk ? t.urdume : 0);
                }
                if (conta) {
                    ocorrencias++;
                    soma += valor;
                    tearesAfetados.add(t.tear);
                }
            });

            const totalTeares = item.teares.size;
            lista.push({
                artigo: item.artigo,
                teares: totalTeares,
                tearesAfetados: tearesAfetados.size,
                pctTeares: totalTeares > 0 ? (tearesAfetados.size / totalTeares) * 100 : 0,
                ocorrencias,
                soma,
                media: ocorrencias > 0 ? soma / ocorrencias : null,
            });
        });

        // participação de cada artigo no total de ocorrências do período
        const total = lista.reduce((acc, l) => acc + l.ocorrencias, 0);
        lista = lista.map((l) => ({ ...l, pctOcorrencias: total > 0 ? (l.ocorrencias / total) * 100 : 0 }));

        if (artigos.length > 0) lista = lista.filter((l) => artigos.includes(l.artigo));
        if (ocultarZerados) lista = lista.filter((l) => l.ocorrencias > 0);

        lista.sort(
            (a, b) =>
                b.ocorrencias - a.ocorrencias ||
                b.soma - a.soma ||
                a.artigo.localeCompare(b.artigo, "pt-BR", { numeric: true })
        );

        return { linhas: lista, totalOcorrencias: total };
    }, [registros, artigos, metrica, limite, ocultarZerados]);

    const limpar = () => {
        setArtigos([]);
        setMetrica("trama");
        setLimite(15);
        setOcultarZerados(false);
    };

    // degradê vermelho proporcional à % de teares afetados
    const corPct = (pct) => {
        const g = 255 - Math.round(135 * (pct / 100));
        return `rgb(255, ${g}, ${g})`;
    };

    return (
        <Panel header="Ocorrências por artigo" toggleable collapsed={true}>
            <style>{`
                .qoa-wrap { overflow-x: auto; }
                .qoa-wrap.qoa-print { overflow: visible; }
                .qoa-table, .qoa-table * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .qoa-table { border-collapse: collapse; font-size: 13px; width: 100%; min-width: 720px; }
                .qoa-table th, .qoa-table td { border: 1px solid #d0d0d0; padding: 5px 8px; text-align: center; white-space: nowrap; }
                .qoa-table thead th { background: #1f3864; color: #fff; font-weight: 700; white-space: normal; }
                .qoa-legenda { font-size: 12px; color: #444; margin-top: 8px; }
            `}</style>

            <div className="p-fluid p-formgrid p-grid p-mb-2">
                <div className="p-field p-col-12 p-md-6">
                    <label>Artigo</label>
                    <MultiSelect
                        value={artigos}
                        options={artigoOptions}
                        onChange={(e) => setArtigos(e.value)}
                        placeholder="Todos os artigos"
                        display="chip"
                        filter
                    />
                </div>
                <div className="p-field p-col-12 p-md-6">
                    <label>Ocorrência com quebras ≥</label>
                    <InputNumber
                        value={limite}
                        onValueChange={(e) => setLimite(e.value)}
                        placeholder="Quantidade de quebras"
                        min={0}
                        showButtons
                    />
                </div>
            </div>

            <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap p-mb-3">
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <span className="p-mr-2">Métrica:</span>
                    <SelectButton value={metrica} options={METRICAS} onChange={(e) => e.value && setMetrica(e.value)} unselectable={false} />
                </div>
                <div className="p-d-flex p-ai-center p-mr-4 p-mb-2">
                    <Checkbox inputId="qoa-zerados" checked={ocultarZerados} onChange={(e) => setOcultarZerados(e.checked)} />
                    <label htmlFor="qoa-zerados" className="p-ml-2">Ocultar artigos sem ocorrência</label>
                </div>
                <div className="p-d-flex p-ai-center p-mb-2">
                    <Button
                        className="p-button-outlined p-mr-2"
                        label="Limpar filtros"
                        icon="pi pi-filter-slash"
                        onClick={limpar}
                        style={{ width: "auto", whiteSpace: "nowrap" }}
                    />
                    <Button
                        label="Imprimir"
                        icon="pi pi-print"
                        onClick={handlePrint}
                        style={{ width: "auto", whiteSpace: "nowrap" }}
                    />
                </div>
            </div>

            <div ref={tabelaRef} style={{ padding: printing ? "20px" : 0 }}>
                {printing && (
                    <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                        Ocorrências por artigo — {data?.periodo}
                    </h2>
                )}

                {linhas.length === 0 ? (
                    <p>Nenhum artigo encontrado com os filtros selecionados.</p>
                ) : (
                    <>
                        <div className={`qoa-wrap ${printing ? "qoa-print" : ""}`}>
                            <table className="qoa-table">
                                <thead>
                                    <tr>
                                        <th>Artigo</th>
                                        <th>Teares no artigo</th>
                                        <th>Teares com ocorrência</th>
                                        <th>% teares afetados</th>
                                        <th>Ocorrências</th>
                                        <th>Soma quebras</th>
                                        <th>Média por ocorrência</th>
                                        <th>% das ocorrências</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linhas.map((l) => (
                                        <tr key={l.artigo}>
                                            <td style={{ fontWeight: 700 }}>{l.artigo}</td>
                                            <td>{l.teares}</td>
                                            <td>{l.tearesAfetados}</td>
                                            <td style={{ background: corPct(l.pctTeares) }}>{Math.round(l.pctTeares)}%</td>
                                            <td>{fmt0.format(l.ocorrencias)}</td>
                                            <td>{fmt0.format(l.soma)}</td>
                                            <td>{l.media === null ? "-" : fmt1.format(l.media)}</td>
                                            <td>{fmt1.format(l.pctOcorrencias)}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="qoa-legenda">
                            Ocorrência: um tear em um turno (dia + turma) com {limite ?? 0} quebras ou mais, avaliando trama e urdume separadamente
                            {metrica === "total" ? " (soma apenas o que atingiu o limite em cada uma)" : ` (${METRICAS.find((m) => m.value === metrica).label.toLowerCase()})`}.
                            Teares no artigo: teares que produziram o artigo no período.
                            % das ocorrências: participação do artigo no total de {fmt0.format(totalOcorrencias)} ocorrências do período.
                        </div>
                    </>
                )}
            </div>
        </Panel>
    );
});